import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, reactive, Ref, ref } from "vue";
import { useRoute } from "vue-router/composables";

import ProfileWrapper from "@/components/profile/ProfileWrapper.vue";
import ExportButton from "@/components/buttons/ExportButton.vue";
import ProfileList from "@/components/lists/ProfileList.vue";
import ProfilePanel from "@/components/profile/ProfilePanel.vue";
import StafferTable from "@/components/tables/StafferTable.vue";
import { ApiService, LogService } from "@/services";
import { VignetteCaucus, VignetteCommittee } from "@/types/nj/Groups";
import { MetadataKeys, VignetteHelpers, VignetteRoutes } from "@/types";
import { useViewAnalytics } from "@/composition/useViewAnalytics";
import NjBtn from "@/components/vuetify-wrappers/NjButton.vue";
import DjangoRouterLink from "@/components/links/DjangoRouterLink.vue";
import { Chip } from "@/types/nj/Chip";
import { ParsedResponse } from "@/types/utils/CaseTypes";
import { VignetteCommitteeStafferResponse } from "@/types/api/SwaggerOutput";
import { MinimumBriefcaseKeys } from "@/components/buttons/AddToBriefcaseButton.vue";
import { UniversalSearchParams } from "@/types/api/SearchEndpointInputs";
import BulkDossierDownloadButton from "@/components/buttons/BulkDossierDownloadButton.vue";
import HierarchyChip from "@/components/profile/HierarchyChip.vue";
import { useAppStore } from "@/pinia/app";

interface Props {
  type: "committee" | "caucus";
}

type CommitteeStaffer = ParsedResponse<VignetteCommitteeStafferResponse>;


export default /*#__PURE__*/_defineComponent({
  __name: 'GroupDetail',
  props: {
    type: null
  },
  setup(__props: any) {

const props = __props as {
  type: "committee" | "caucus";
};


const appStore = useAppStore();
const maxBatchProfileDownloads = computed(
  () => appStore.maxBatchProfileDownloads
);
const isCaucus = computed(() => props.type === "caucus");
const isCommittee = computed(() => props.type === "committee");

const pageName = ref("");

const defaultOpenPanels = [
  "Contact Information",
  "Subcommittees",
  "Republican Members",
  "Democratic Members",
  "Independent Members",
];

const route = useRoute();
const groupId = computed(() => +route.params.id);

const display404 = ref(false);
const display500 = ref(false);

const group: Ref<VignetteCommittee | VignetteCaucus | null> = ref(null);
const searchParams = ref<Partial<UniversalSearchParams>>({
  only_kw: "true",
  profile_types: "8,9,10,12",
});
const independentPartyNames = computed(() => {
  const independentMembers = group.value?.members.independent;
  if (!independentMembers) return [];
  const result: string[] = [];
  for (const member of independentMembers) {
    if (result.includes(member.politicalParty)) continue;
    result.push(member.politicalParty);
  }
  return result;
});

const briefcaseObj: MinimumBriefcaseKeys = reactive({
  id: 0,
  briefcaseItemId: 0,
  addedToBriefcase: false,
  longName: "",
  preferredNameDisplay: "",
  positionSummary: "Caucus",
  recordType: "caucus", // set default value so typescript doesn't yell at us
});

const presentationCenterLink = computed(
  () => group.value?.presentationCenterUrl ?? ""
);

const groupIsCommittee = (
  g: VignetteCommittee | VignetteCaucus | null
): g is VignetteCommittee => {
  return g?.staffers !== undefined;
};
// const groupIsCaucus = (
//   g: VignetteCommittee | VignetteCaucus | null
// ): g is VignetteCaucus => {
//   return g?.staffers === undefined;
// };

const { setAnalyticsMetadata, pushPageViewEvent } = useViewAnalytics(pageName);

const setMetaData = (groupName: "caucus" | "committee", id: number) => {
  setAnalyticsMetadata(MetadataKeys.PAGE_TYPE, "Database");
  setAnalyticsMetadata(MetadataKeys.SITESECTION, "AlmanacPlus");
  setAnalyticsMetadata(MetadataKeys.CMS_CONTENT_ID, `${groupName}-${id}`);
  pushPageViewEvent();
};

const allStaffers = computed<CommitteeStaffer[]>(() => {
  // type guard so that we can access the staffers property
  if (groupIsCommittee(group.value)) {
    return group.value.staffers;
  } else {
    return [];
  }
});

const majorityStaffers = computed(() =>
  allStaffers.value.filter((s) => s.majmin == "MAJ")
);
const minorityStaffers = computed(() =>
  allStaffers.value.filter((s) => s.majmin == "MIN")
);
const otherStaffers = computed(() =>
  allStaffers.value.filter((s) => s.majmin == "OTH")
);

const chips = computed<Chip[]>(() => {
  return group.value?.chips ?? [];
});

const getCommittee = () => {
  ApiService.getCommittee(groupId.value)
    .then((response: VignetteCommittee) => {
      group.value = response;
      pageName.value = response.shortName;
      briefcaseObj.id = response.id;
      briefcaseObj.briefcaseItemId = response.briefcaseItemId;
      briefcaseObj.addedToBriefcase = response.addedToBriefcase;
      briefcaseObj.longName = response.longName;
      briefcaseObj.preferredNameDisplay = response.longName;
      briefcaseObj.positionSummary = "Committee";
      briefcaseObj.recordType = response.recordType ?? "committee";
      setMetaData("committee", response.id);
      searchParams.value.committees = response.id.toString();
    })
    .catch((error) => {
      LogService.warn(
        `CommitteeDetail.vue: Error retrieving committee from groupId: ${groupId.value}`
      );
      const status = error?.status || error?.response?.status;
      if (status && Number(status) >= 500) {
        display500.value = true;
      } else {
        display404.value = true;
      }
    });
};

const getCaucus = () => {
  ApiService.getCaucus(groupId.value)
    .then((response: VignetteCaucus) => {
      group.value = response;
      pageName.value = response.shortName;
      briefcaseObj.id = response.id;
      briefcaseObj.briefcaseItemId = response.briefcaseItemId;
      briefcaseObj.addedToBriefcase = response.addedToBriefcase;
      briefcaseObj.longName = response.longName;
      briefcaseObj.preferredNameDisplay = response.longName;
      briefcaseObj.positionSummary = "Caucus";
      briefcaseObj.recordType = response.recordType ?? "caucus";
      setMetaData("caucus", response.id);
      searchParams.value.caucuses = response.id.toString();
    })
    .catch((error) => {
      LogService.warn(
        `CommitteeDetail.vue: Error retrieving caucus from caucusId: ${groupId.value}`
      );
      const status = error?.status || error?.response?.status;
      if (status && Number(status) >= 500) {
        display500.value = true;
      } else {
        display404.value = true;
      }
    });
};

onBeforeMount(() => {
  if (isCommittee.value) {
    getCommittee();
  } else if (isCaucus.value) {
    getCaucus();
  }
});

return { __sfc: true,props, appStore, maxBatchProfileDownloads, isCaucus, isCommittee, pageName, defaultOpenPanels, route, groupId, display404, display500, group, searchParams, independentPartyNames, briefcaseObj, presentationCenterLink, groupIsCommittee, setAnalyticsMetadata, pushPageViewEvent, setMetaData, allStaffers, majorityStaffers, minorityStaffers, otherStaffers, chips, getCommittee, getCaucus, ProfileWrapper, ExportButton, ProfileList, ProfilePanel, StafferTable, VignetteHelpers, VignetteRoutes, NjBtn, DjangoRouterLink, BulkDossierDownloadButton, HierarchyChip }
}

})